import {
  ExperimentNames,
  getEventDescription,
  getEventId,
  getEventMainImage,
  getEventTitle,
  getFormattedFullLocation,
} from '@wix/wix-events-commons-statics'
import {ChevronUp} from '@wix/wix-ui-icons-common'
import {useExperiments, useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React from 'react'
import {getEventDateInformation, isMultiDay} from '../../../../../../../../../../../commons/selectors/event'
import {useWidgetActions, useWidgetState} from '../../../../../../../../hooks/state-provider'
import {useVisibilityStyles} from '../../../../../../../../hooks/use-visibility-styles'
import {getSelectedDate} from '../../../../../../../../selectors/calendar-layout'
import {getEventBadge, getMultiDayText, getRibbonData} from '../../../../../../../../selectors/events'
import {isMembersVisible, isRibbonVisible} from '../../../../../../../../selectors/settings'
import {EventBadge} from '../../../../../../../event-badge'
import {EventImage} from '../../../../../../../event-image'
import {LinkToPage} from '../../../../../../../link-to-page'
import {Members} from '../../../../../../../members'
import {Ribbon} from '../../../../../../../ribbon'
import {RsvpButton} from '../../../../../../../rsvp-button'
import {SocialBar} from '../../../../../../../social-bar'
import {MobileMonthlyEventDetailsProps} from './interfaces'
import s from './mobile-monthly-event-details.scss'

export const MobileMonthlyEventDetails = ({event}: MobileMonthlyEventDetailsProps) => {
  const {t} = useTranslation()
  const {
    allBreakpoints: {isEventBadgeInEventInfo},
  } = useVisibilityStyles()
  const {closeMonthlyCalendarEvent} = useWidgetActions()
  const {experiments} = useExperiments()

  const hasImage = !!getEventMainImage(event)
  const eventId = getEventId(event)
  const selectedDate = useWidgetState(getSelectedDate)
  const multiDayText = useWidgetState(state => getMultiDayText(state, eventId, selectedDate, t))
  const hasRibbon = useWidgetState(state => getRibbonData(state, event)).visible
  const eventBadge = useWidgetState(state => getEventBadge(state, event))
  const showMembers = useWidgetState(state => isMembersVisible(state, event))
  const showRibbon = useWidgetState(state => isRibbonVisible(state, event))
  const date = useWidgetState(state => getEventDateInformation(state, eventId)).fullDate
  const multiDay = useWidgetState(state => isMultiDay(state, event))
  const mobileCalendarRewireEnabled = experiments.enabled(ExperimentNames.MobileCalendarRewire)

  const closeEventDetails = () => {
    closeMonthlyCalendarEvent()
  }

  return (
    <div
      className={classNames(s.container, {
        [s.calendar]: !mobileCalendarRewireEnabled,
        [s.rewire]: mobileCalendarRewireEnabled,
      })}
    >
      <div data-hook={`event-details-${eventId}`} className={s.eventInfo} onClick={closeEventDetails}>
        {isEventBadgeInEventInfo(eventBadge?.type) && (
          <EventBadge event={event} className={classNames({[s.eventBadge]: !showRibbon || !hasRibbon})} />
        )}
        {showRibbon && <Ribbon event={event} className={s.ribbon} />}
        {multiDay ? (
          <div className={s.time}>{multiDayText}</div>
        ) : (
          <div data-hook="date" className={s.fullDate}>
            {date}
          </div>
        )}
        <div
          data-hook="title"
          className={classNames({
            [s.multiDayTitle]: multiDay,
            [s.title]: !multiDay,
          })}
        >
          <LinkToPage event={event}>{getEventTitle(event)}</LinkToPage>
          <div className={s.chevron}>
            <ChevronUp size="1.2em" />
          </div>
        </div>
      </div>
      {hasImage && (
        <div className={s.image}>
          <EventImage event={event} />
        </div>
      )}
      {multiDay && (
        <div data-hook="date" className={mobileCalendarRewireEnabled ? s.multiDayFullDate : s.fullDate}>
          {date}
        </div>
      )}
      <div data-hook="location" className={s.location}>
        {getFormattedFullLocation(event)}
      </div>
      {showMembers && (
        <div data-hook="members" className={s.members}>
          <Members event={event} />
        </div>
      )}
      {getEventDescription(event) && (
        <div data-hook="description" className={s.description}>
          {getEventDescription(event)}
        </div>
      )}
      <div data-hook="share" className={s.share}>
        {!mobileCalendarRewireEnabled && <div className={s.shareText}>{t('shareEventText')}</div>}
        <SocialBar
          row
          hideTitle
          newColors={mobileCalendarRewireEnabled}
          t={t}
          spacing={7}
          event={event}
          iconFontSize={14}
        />
      </div>
      <RsvpButton
        event={event}
        dataHook="rsvp-button"
        minWidth={88}
        maxWidth={170}
        minHeight={32}
        additionalStyle={{fontSize: 14}}
        containerClassName={s.button}
      />
    </div>
  )
}
